import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import { MapPin, Heart, Users, Trophy, CheckCircle2 } from 'lucide-react';

const getMedal = (i) => {
  if (i === 0) return '🥇';
  if (i === 1) return '🥈';
  if (i === 2) return '🥉';
  return i + 1;
};

function LeaderRow({ rank, name, country, value, unit, userId, finished }) {
  const content = (
    <div
      className={`flex items-center justify-between p-3 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors ${userId ? 'cursor-pointer' : ''}`}
      data-testid={`leader-row-${rank}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
          rank < 3 ? 'bg-orange-100 text-orange-700' : 'bg-stone-200 text-stone-600'
        }`}>
          {rank < 3 ? getMedal(rank) : rank + 1}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className={`text-sm font-medium truncate ${userId ? 'text-orange-700 hover:underline' : 'text-stone-900'}`}>
              {name}
            </p>
            {finished && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700"
                data-testid="leader-finished-badge"
                title="Finished this challenge"
              >
                <CheckCircle2 className="w-3 h-3" /> Finished
              </span>
            )}
          </div>
          {country && <p className="text-xs text-stone-400">{country}</p>}
        </div>
      </div>
      <p className="text-sm font-bold text-stone-900 flex-shrink-0 ml-2">{value} {unit}</p>
    </div>
  );
  if (userId) {
    return <Link to={`/fundraise/${userId}`} data-testid={`leader-link-${userId}`}>{content}</Link>;
  }
  return content;
}

export default function LeaderboardPage() {
  // The Leaderboard now drives every list off a SELECTED challenge.
  // `selectedChallengeId === ''` means "Overall — sum across challenges"
  // (Bite 2 will fill this in with a dedicated endpoint; today it falls
  // back to the latest-challenge view via the backend default).
  const [challenges, setChallenges] = useState([]);
  const [selectedChallengeId, setSelectedChallengeId] = useState(null);
  const [distLeaders, setDistLeaders] = useState([]);
  const [raisedLeaders, setRaisedLeaders] = useState([]);
  const [teamDist, setTeamDist] = useState([]);
  const [teamRaised, setTeamRaised] = useState([]);
  const [communityStats, setCommunityStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load challenges once. Default the dropdown to the most recently created
  // challenge so existing walkers see "their" leaderboard on page load.
  // 'overall' is a synthetic option that aggregates final_km / final_raised
  // across every completed participation, so multi-challenge walkers and
  // teams (Bite 2) get a single cross-challenge ranking.
  // TODO: re-enable the Overall option in the dropdown once the legacy-
  // participation backfill lands (populate final_km from activities and
  // stamp team_id from users.team_id where missing). Until then the
  // OVERALL constant + the if(chId===OVERALL) branch below are
  // intentional dead code so the feature can be flipped back on cheaply.
  const OVERALL = 'overall';
  useEffect(() => {
    Promise.all([
      api.get('/challenges'),
      api.get('/leaderboards/default-challenge').catch(() => ({ data: {} })),
    ]).then(([r, d]) => {
      const list = Array.isArray(r.data) ? r.data : [];
      // Newest first
      const sorted = [...list].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      setChallenges(sorted);
      if (sorted.length > 0 && selectedChallengeId === null) {
        const def = sorted.find(c => c.id === d.data?.challenge_id);
        setSelectedChallengeId((def || sorted[0]).id);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = useCallback(async (chId) => {
    setLoading(true);
    if (chId === OVERALL) {
      // Cross-challenge view: one aggregated payload for walkers + teams.
      // The walkers endpoint sorts by total_raised on the server; we
      // re-sort client-side for the Distance tab so km tab is honest.
      try {
        const [w, t] = await Promise.all([
          api.get('/leaderboards/overall/walkers'),
          api.get('/leaderboards/overall/teams'),
        ]);
        const walkers = w.data || [];
        const teams = t.data || [];
        // Distance tab: sort walkers by total_km desc
        const byKm = [...walkers].sort((a, b) => (b.total_km || 0) - (a.total_km || 0));
        // Distance for teams: sort by total_km
        const teamByKm = [...teams].sort((a, b) => (b.total_km || 0) - (a.total_km || 0));
        setDistLeaders(byKm);
        setRaisedLeaders(walkers); // already sorted by total_raised
        setTeamDist(teamByKm);
        setTeamRaised(teams); // already sorted by total_raised
        // No per-challenge community stats for overall view.
        setCommunityStats(null);
      } catch (_) {
        // soft-fail; leave previous data on screen
      } finally {
        setLoading(false);
      }
      return;
    }
    const qs = chId ? `?challenge_id=${encodeURIComponent(chId)}` : '';
    try {
      const [d, r, td, tr, cs] = await Promise.all([
        api.get(`/leaderboards/distance${qs}`),
        api.get(`/leaderboards/raised${qs}`),
        api.get(`/leaderboards/teams/distance${qs}`),
        api.get(`/leaderboards/teams/raised${qs}`),
        api.get(`/leaderboards/community-stats${qs}`),
      ]);
      setDistLeaders(d.data || []);
      setRaisedLeaders(r.data || []);
      setTeamDist(td.data || []);
      setTeamRaised(tr.data || []);
      setCommunityStats(cs.data || null);
    } catch (_) {
      // soft-fail; leave previous data on screen
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedChallengeId === null) return;
    fetchAll(selectedChallengeId);
  }, [selectedChallengeId, fetchAll]);


  return (
    <div className="container-app py-8 md:py-12" data-testid="leaderboard-page">
      <div className="text-center mb-6">
        <Trophy className="w-10 h-10 text-orange-600 mx-auto mb-3" />
        <h1 className="text-2xl md:text-3xl font-bold text-stone-900">Leaderboard</h1>
        {communityStats ? (
          <p className="text-stone-500 mt-1" data-testid="leaderboard-community-stats">
            {Number(communityStats.walkers || 0).toLocaleString()} Walker{communityStats.walkers === 1 ? '' : 's'}
            {' \u00B7 '}
            {Number(communityStats.teams || 0).toLocaleString()} Team{communityStats.teams === 1 ? '' : 's'}
          </p>
        ) : (
          <p className="text-stone-500 mt-1">&nbsp;</p>
        )}
      </div>

      {/* Challenge picker — always visible so the admin/walker can
          always see and switch which challenge they're viewing. */}
      {challenges.length >= 1 && (
        <div className="max-w-2xl mx-auto mb-5 flex items-center justify-center gap-2 flex-wrap">
          <label className="text-xs text-stone-500 uppercase tracking-wider" htmlFor="challenge-select">
            Challenge
          </label>
          <Select
            value={selectedChallengeId || ''}
            onValueChange={(v) => setSelectedChallengeId(v)}
          >
            <SelectTrigger className="w-auto min-w-[16rem] h-9 text-sm rounded-full bg-white" data-testid="leaderboard-challenge-select">
              <SelectValue placeholder="Select a challenge" />
            </SelectTrigger>
            <SelectContent>
              {challenges.map(c => (
                <SelectItem key={c.id} value={c.id} data-testid={`challenge-option-${c.id}`}>
                  {c.name}{c.total_distance_km ? ` (${c.total_distance_km} km)` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <Tabs defaultValue="distance" className="max-w-2xl mx-auto">
        <TabsList className="grid grid-cols-4 bg-stone-100 rounded-xl p-1 mb-6">
          <TabsTrigger value="distance" className="rounded-lg text-xs sm:text-sm data-[state=active]:bg-white" data-testid="tab-distance">
            <MapPin className="w-3 h-3 mr-1 hidden sm:inline" /> Distance
          </TabsTrigger>
          <TabsTrigger value="raised" className="rounded-lg text-xs sm:text-sm data-[state=active]:bg-white" data-testid="tab-raised">
            <Heart className="w-3 h-3 mr-1 hidden sm:inline" /> Raised
          </TabsTrigger>
          <TabsTrigger value="team-distance" className="rounded-lg text-xs sm:text-sm data-[state=active]:bg-white" data-testid="tab-team-distance">
            <Users className="w-3 h-3 mr-1 hidden sm:inline" /> Team km
          </TabsTrigger>
          <TabsTrigger value="team-raised" className="rounded-lg text-xs sm:text-sm data-[state=active]:bg-white" data-testid="tab-team-raised">
            <Users className="w-3 h-3 mr-1 hidden sm:inline" /> Team $
          </TabsTrigger>
        </TabsList>

        {loading ? (
          <Card className="bg-white rounded-2xl border border-stone-100">
            <CardContent className="p-12 flex items-center justify-center">
              <div className="w-6 h-6 border-4 border-orange-600 border-t-transparent rounded-full animate-spin" />
            </CardContent>
          </Card>
        ) : (
          <>
            <TabsContent value="distance">
              <Card className="bg-white rounded-2xl border border-stone-100">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-stone-900 mb-4">Top Walkers by Distance</h3>
                  {distLeaders.length === 0 ? (
                    <p className="text-stone-400 text-center py-6">No entries yet</p>
                  ) : (
                    <div className="space-y-2">
                      {distLeaders.map((l, i) => (
                        <LeaderRow
                          key={l.user_id}
                          rank={i}
                          name={l.display_name}
                          country={l.country}
                          value={l.total_km}
                          unit="km"
                          userId={l.user_id}
                          finished={!!l.finished}
                        />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="raised">
              <Card className="bg-white rounded-2xl border border-stone-100">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-stone-900 mb-4">Top Fundraisers</h3>
                  {raisedLeaders.length === 0 ? (
                    <p className="text-stone-400 text-center py-6">No entries yet</p>
                  ) : (
                    <div className="space-y-2">
                      {raisedLeaders.map((l, i) => (
                        <LeaderRow
                          key={l.user_id}
                          rank={i}
                          name={l.display_name}
                          country={l.country}
                          value={`$${l.total_raised}`}
                          unit=""
                          userId={l.user_id}
                          finished={!!l.finished}
                        />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="team-distance">
              <Card className="bg-white rounded-2xl border border-stone-100">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-stone-900 mb-4">Top Teams by Distance</h3>
                  {teamDist.length === 0 ? (
                    <p className="text-stone-400 text-center py-6">No entries yet</p>
                  ) : (
                    <div className="space-y-2">
                      {teamDist.map((t, i) => (
                        <LeaderRow key={t.team_id || t.name} rank={i} name={t.name} country={`${t.members_count} members`} value={t.total_km} unit="km" />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="team-raised">
              <Card className="bg-white rounded-2xl border border-stone-100">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-stone-900 mb-4">Top Teams by Fundraising</h3>
                  {teamRaised.length === 0 ? (
                    <p className="text-stone-400 text-center py-6">No entries yet</p>
                  ) : (
                    <div className="space-y-2">
                      {teamRaised.map((t, i) => (
                        <LeaderRow key={t.team_id || t.name} rank={i} name={t.name} country={`${t.members_count} members`} value={`$${t.total_raised}`} unit="" />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}
